import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { formatCurrency } from 'tio-ui/format';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import CircularProgress from 'tio-ui/components/circular-progress';
import StatCard from 'tio-ui/components/stat-card';
import PageHero from 'tio-ui/components/page-hero';
import Card from 'tio-ui/components/card';
import {
  PhVault,
  PhShieldCheck,
  PhFileText,
  PhUploadSimple,
  PhDownloadSimple,
  PhCheckCircle,
  PhWarning,
  PhClock,
  PhCalendar,
  PhArrowRight,
  PhInfo,
  PhTarget,
} from 'ember-phosphor-icons';
import InfoRow from 'tio-ui/components/info-row';
import SectionHeader from 'tio-ui/components/section-header';
import type { PslfRiskFactor, PslfVaultSummary } from 'tio-employee/services/career-pathways-api';

const RISK_SEVERITY_CLASSES: Record<
  PslfRiskFactor['severity'],
  { container: string; icon: string; title: string; description: string }
> = {
  high: {
    container: 'border rounded-xl p-4 tio-bg-danger-soft tio-border-danger',
    icon: 'shrink-0 tio-fg-danger',
    title: 'font-semibold tio-fg-danger-h',
    description: 'mt-1 tio-fg-danger-body',
  },
  medium: {
    container: 'border rounded-xl p-4 tio-bg-warning-soft tio-border-warning',
    icon: 'shrink-0 tio-fg-warning',
    title: 'font-semibold tio-fg-warning-h',
    description: 'mt-1 tio-fg-warning-body',
  },
  low: {
    container: 'border rounded-xl p-4 tio-bg-info-soft tio-border-info',
    icon: 'shrink-0 tio-fg-info',
    title: 'font-semibold tio-fg-info-h',
    description: 'mt-1 tio-fg-info-body',
  },
};

interface PslfVaultIndexModel {
  vault: PslfVaultSummary;
}

interface PslfVaultIndexSignature {
  Args: {
    model: PslfVaultIndexModel;
  };
}

export default class CareerPathwaysPslfVaultIndex extends Component<PslfVaultIndexSignature> {
  @tracked showUploadModal = false;

  get vault() {
    return this.args.model.vault;
  }

  get progress() {
    return this.vault.progress;
  }

  get progressPercent() {
    return Math.round((this.progress.qualifyingPayments / 120) * 100);
  }

  get complianceColor() {
    if (this.vault.complianceScore >= 80) return 'emerald';
    if (this.vault.complianceScore >= 60) return 'amber';
    return 'red';
  }

  formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  getStatusColor = (status: string) => {
    switch (status) {
      case 'certified':
        return 'success';
      case 'submitted':
      case 'processing':
        return 'warning';
      case 'rejected':
        return 'danger';
      default:
        return 'default';
    }
  };

  getRiskSeverityClasses = (severity: PslfRiskFactor['severity']) => {
    return RISK_SEVERITY_CLASSES[severity];
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="data-audit w-full min-w-0 max-w-6xl mx-auto">
      {{! Hero Section }}
      <PageHero @gradient="brand" @rounded="2xl">
        <:icon>
          <PhVault @size={{32}} />
          <Heading @level={{1}} class="tio-fg-inverse">
            PSLF Compliance Vault
          </Heading>
        </:icon>
        <:default>
          <Text @size="lg" class="tio-pslf-vault__description">
            Audit-proof documentation storage for your Public Service Loan Forgiveness journey.
            Track progress, store certifications, and stay on track for forgiveness.
          </Text>

          {{! Progress Ring }}
          <div class="tio-row-extra-loose">
            <CircularProgress
              @value={{this.progress.qualifyingPayments}}
              @max={{120}}
              @color="emerald"
              @size="lg"
              @label="PSLF payment progress"
              class="tio-pslf-vault__progress-ring"
            >
              <div class="text-center">
                <Text
                  @weight="bold"
                  class="text-2xl tio-fg-inverse"
                >{{this.progress.qualifyingPayments}}</Text>
                <Text @size="xs" class="tio-pslf-vault__progress-total">/120</Text>
              </div>
            </CircularProgress>
            <div>
              <Text @weight="semibold" class="tio-fg-inverse">
                {{this.progress.paymentsRemaining}}
                payments remaining
              </Text>
              <Text @size="sm" class="tio-pslf-vault__estimate">
                Estimated forgiveness:
                {{this.formatDate this.progress.estimatedForgivenessDate}}
              </Text>
              <Text @size="sm" @weight="medium" class="tio-pslf-vault__projection">
                Projected forgiveness:
                {{formatCurrency this.progress.projectedForgivenessAmount}}
              </Text>
            </div>
          </div>
        </:default>
      </PageHero>

      {{! Quick Stats }}
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <StatCard
          @icon={{PhShieldCheck}}
          @value="{{this.vault.complianceScore}}%"
          @label="Compliance Score"
          @trend={{if (isGreaterOrEqual this.vault.complianceScore 80) "up" "stable"}}
          @trendText={{if
            (isGreaterOrEqual this.vault.complianceScore 80)
            "Audit-ready"
            "Needs attention"
          }}
          @color={{this.complianceColor}}
        />
        <StatCard
          @icon={{PhFileText}}
          @value="{{this.vault.totalDocuments}}"
          @label="Documents Stored"
          @color="violet"
        />
        <StatCard
          @icon={{PhCheckCircle}}
          @value="{{this.vault.certifiedPeriods}}"
          @label="Certified Periods"
          @color="emerald"
        />
        <StatCard
          @icon={{PhCalendar}}
          @value={{this.formatDate this.progress.nextCertificationDue}}
          @label="Next ECF Due"
          @color="amber"
        />
      </div>

      {{! Risk Factors }}
      {{#if this.progress.riskFactors.length}}
        <div class="mt-6 space-y-3">
          {{#each this.progress.riskFactors as |risk|}}
            {{#let (this.getRiskSeverityClasses risk.severity) as |classes|}}
              <div class={{classes.container}}>
                <div class="tio-row-top">
                  <PhWarning @size={{24}} class={{classes.icon}} />
                  <div class="flex-1">
                    <Text class={{classes.title}}>
                      {{risk.title}}
                    </Text>
                    <Text @size="sm" class={{classes.description}}>
                      {{risk.description}}
                    </Text>
                  </div>
                  {{#if risk.actionUrl}}
                    <LinkTo @route={{risk.actionUrl}}>
                      <Button @intent="default" @size="sm" @appearance="outlined">
                        {{risk.action}}
                      </Button>
                    </LinkTo>
                  {{/if}}
                </div>
              </div>
            {{/let}}
          {{/each}}
        </div>
      {{/if}}

      {{! Compliance Gaps }}
      {{#if this.vault.complianceGaps.length}}
        <div class="mt-6 tio-bg-warning-soft border tio-border-warning rounded-xl p-4">
          <div class="tio-row-tight mb-3">
            <PhInfo @size={{20}} class="tio-fg-warning" />
            <Text @weight="semibold" class="tio-fg-warning-h">Compliance Gaps</Text>
          </div>
          <ul class="space-y-2">
            {{#each this.vault.complianceGaps as |gap|}}
              <li class="tio-row-tight tio-fg-warning-body text-sm">
                <PhClock @size={{16}} />
                {{gap}}
              </li>
            {{/each}}
          </ul>
        </div>
      {{/if}}

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        {{! Main Content - Documents }}
        <div class="lg:col-span-2">
          <Card @padding="none" class="shadow-sm">
            <div class="tio-row-between p-4 border-b tio-border-subtle">
              <Heading @level={{2}} @size="lg">Your Documents</Heading>
              <Button @intent="primary" @size="sm">
                <PhUploadSimple @size={{16}} class="mr-1" />
                Upload Document
              </Button>
            </div>

            <div class="divide-y tio-divide-subtle">
              {{#each this.vault.documents as |doc|}}
                <div class="p-4 tio-row-loose hover:tio-bg-sunken">
                  <div class="p-2 tio-bg-brand-soft rounded-lg">
                    <PhFileText @size={{24}} class="tio-fg-brand" />
                  </div>
                  <div class="tio-fill-min">
                    <Text @weight="medium" class="truncate">{{doc.filename}}</Text>
                    <div class="tio-row text-sm tio-fg-muted">
                      <span>{{doc.employerName}}</span>
                      <span>•</span>
                      <span>{{this.formatDate doc.uploadedAt}}</span>
                      {{#if doc.paymentsCertified}}
                        <span>•</span>
                        <span class="tio-fg-success">{{doc.paymentsCertified}}
                          payments certified</span>
                      {{/if}}
                    </div>
                  </div>
                  <Chip @intent={{this.getStatusColor doc.status}} @size="sm">
                    {{doc.status}}
                  </Chip>
                  <button type="button" class="p-2 hover:tio-bg-sunken rounded-lg">
                    <PhDownloadSimple @size={{20}} class="tio-fg-muted" />
                  </button>
                </div>
              {{/each}}
            </div>
          </Card>
        </div>

        {{! Sidebar - Milestones }}
        <div class="space-y-6">
          {{! Milestone Tracker }}
          <Card @padding="sm" class="shadow-sm">
            <SectionHeader @icon={{PhTarget}} @title="Milestones" @iconClass="tio-fg-brand" />

            <div class="space-y-3">
              {{#each this.progress.milestones as |milestone|}}
                <div class="tio-row">
                  <div
                    class="w-8 h-8 rounded-full tio-row-center
                      {{if milestone.reached 'tio-bg-success-soft' 'tio-bg-sunken'}}"
                  >
                    {{#if milestone.reached}}
                      <PhCheckCircle @size={{18}} class="tio-fg-success" />
                    {{else}}
                      <span class="tio-text-xs-medium tio-fg-muted">{{milestone.payments}}</span>
                    {{/if}}
                  </div>
                  <div class="flex-1">
                    <Text
                      @size="sm"
                      class="{{if
                          milestone.reached
                          'tio-fg-success-body font-medium'
                          'tio-fg-secondary'
                        }}"
                    >
                      {{milestone.label}}
                    </Text>
                    {{#if milestone.reachedDate}}
                      <Text @size="xs" class="tio-fg-muted">{{this.formatDate
                          milestone.reachedDate
                        }}</Text>
                    {{/if}}
                  </div>
                </div>
              {{/each}}
            </div>
          </Card>

          {{! Employer Status }}
          <Card @padding="sm" class="shadow-sm">
            <SectionHeader
              @icon={{PhShieldCheck}}
              @title="Employer Status"
              @iconClass="tio-fg-success"
            />

            <div class="space-y-3">
              <InfoRow @label="Employer" @value={{this.progress.currentEmployerName}} />
              <InfoRow @label="Qualifies?">
                <Chip
                  @intent={{if this.progress.currentEmployerQualifies "success" "danger"}}
                  @size="sm"
                >
                  {{if this.progress.currentEmployerQualifies "Yes" "No"}}
                </Chip>
              </InfoRow>
              <InfoRow
                @label="Start Date"
                @value={{this.formatDate this.progress.currentEmployerStartDate}}
              />
              <InfoRow @label="Last Certified">
                <Text @weight="medium">
                  {{#if this.progress.lastCertificationDate}}
                    {{this.formatDate this.progress.lastCertificationDate}}
                  {{else}}
                    Not yet
                  {{/if}}
                </Text>
              </InfoRow>
            </div>
          </Card>

          {{! Quick Actions }}
          <div class="tio-gradient-brand-soft rounded-xl border tio-border-brand p-4">
            <Heading @level={{3}} class="mb-3">Quick Actions</Heading>
            <div class="space-y-2">
              <Button
                @intent="primary"
                @icon={{PhFileText}}
                @label="Generate ECF Form"
                class="w-full justify-center"
              />
              <LinkTo @route="authenticated.reimagined.pslf">
                <Button
                  @intent="default"
                  @appearance="outlined"
                  @icon={{PhArrowRight}}
                  @iconPlacement="end"
                  @label="View Full PSLF Dashboard"
                  class="w-full justify-center"
                />
              </LinkTo>
            </div>
          </div>
        </div>
      </div>
    </VStack>
  </template>
}

function isGreaterOrEqual(a: number, b: number): boolean {
  return a >= b;
}
